import { TZDate, TZDateMini } from '@date-fns/tz';
import {
  addDays,
  addWeeks,
  compareAsc,
  eachDayOfInterval,
  endOfDay,
  endOfWeek,
  intervalToDuration,
  isBefore,
  isEqual,
  isSameDay,
  isWithinInterval,
  lastDayOfMonth,
  lastDayOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

import { getTimezone } from '@dynatrace-sdk/user-preferences';

import type { TimeValue, TimeframeV2 } from '../../../core/types/time.js';
import { removePrecision } from '../../date-time-range-picker/helpers.js';
import {
  _getTimeValueFromSimpleValue,
  _sortTimeframeV2Values,
} from '../../forms-core/time/helpers.js';
import { isExpressionString } from '../../forms-core/time/timeParser.js';
import {
  StringTimeOffset,
  isTimeframeV2,
} from '../../forms-core/time/types.js';
import type {
  CalendarProps,
  CalendarStrictValueType,
  CalendarType,
} from '../Calendar.js';
import type { ViewItem } from '../CalendarItem.js';
import type { _Day } from '../hooks/useSelectDay.js';
import {
  NUMBER_OF_WEEKDAYS,
  NUMBER_OF_WEEK_ROWS,
} from '../types/calendarConfiguration.js';
import type { FirstDayOfWeek, CalendarState } from '../types/shared-types.js';

/**
 * Get days to render in the select date view.
 * @internal
 */
export function getDays(
  activeDate: TZDate,
  weekStartsOn: FirstDayOfWeek,
): _Day[] {
  const monthStart = startOfMonth(activeDate);

  const firstWeekStart = startOfWeek(monthStart, {
    weekStartsOn,
  });

  const monthEnd = lastDayOfMonth(monthStart);
  const lastWeekEnd = lastDayOfWeek(monthEnd, { weekStartsOn });

  // Get days of previous month to fill empty spots of the date grid
  let prevMonthDays = getSiblingMonthDays(monthStart, firstWeekStart, -1);

  const currentMonthDays = toDayArray(
    eachDayOfInterval({
      start: monthStart,
      end: monthEnd,
    }),
    true,
  );

  // Get days of next month to fill empty spots of the date grid
  const nextMonthDays = getSiblingMonthDays(monthEnd, lastWeekEnd, 1);

  const days = [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];

  // Depending on the start/end day of the month, we might need additional rows
  // to fill up all the empty spaces of the date grid
  while (days.length < NUMBER_OF_WEEKDAYS * NUMBER_OF_WEEK_ROWS) {
    if (!prevMonthDays.length) {
      // Add additional row of sibling month days before the current month
      prevMonthDays = toDayArray(
        eachDayOfInterval({
          // Typecast to TZDate since TypeScript doesn't infer addWeeks return type directly
          start: addWeeks<TZDate, TZDate>(firstWeekStart, -1),
          end: addDays<TZDate, TZDate>(firstWeekStart, -1),
        }),
      );

      days.unshift(...prevMonthDays);
    } else {
      // Add additional row of sibling month days after the current month
      days.push(
        ...toDayArray(
          eachDayOfInterval({
            // Typecast to TZDate since TypeScript doesn't infer addWeeks return type directly
            start: addDays<TZDate, TZDate>(lastWeekEnd, 1),
            end: addWeeks<TZDate, TZDate>(lastWeekEnd, 1),
          }),
        ),
      );
    }
  }

  return days;
}

/** Get previous or next month's days to fill the current date select view with the fixed number of rows. */
function getSiblingMonthDays(
  monthStartEnd: TZDate,
  weekStartEnd: TZDate,
  direction: 1 | -1,
): _Day[] {
  // No need to fill empty spots if month start and week start are equal
  if (isEqual(monthStartEnd, weekStartEnd)) {
    return [];
  }

  // Get first/last day of sibling month
  const siblingMonthStartEnd = addDays(monthStartEnd, direction);

  // The range start date must be prior to the range end date -> sort dates
  const sortedDays = [siblingMonthStartEnd, weekStartEnd].sort((a, b) =>
    compareAsc(a, b),
  );

  // Return array with days to fill up the empty spots of the currently displayed month
  return toDayArray(
    eachDayOfInterval({
      start: sortedDays[0],
      end: sortedDays[1],
    }),
  );
}

/** Map array of dates to an array of Day objects */
function toDayArray(
  array: TZDate[],
  isCurrentMonthDay: boolean | undefined = false,
): _Day[] {
  return array.map(
    (date): _Day => ({
      date,
      isCurrentMonthDay,
    }),
  );
}

/**
 * Tries to convert the given value to the proper TimeValue / TimeframeV2 structure. If not possible it returns null.
 * @internal
 */
export function convertToCalendarStructure<Type extends CalendarType>(
  timeOffset: StringTimeOffset,
  type: CalendarType = 'single',
  value?: CalendarStrictValueType<Type>,
  min?: string,
  max?: string,
  precision?: CalendarProps<Type>['precision'],
): TimeValue | Partial<TimeframeV2> | null {
  if (type === 'single' && !value) {
    return null;
  }

  if (typeof value === 'string') {
    return (
      _getTimeValueFromSimpleValue(
        value,
        timeOffset,
        undefined,
        undefined,
        min,
        max,
        precision,
      ) ?? null
    );
  }

  if (value) {
    const from = _getTimeValueFromSimpleValue(
      value.from?.value,
      timeOffset,
      'from',
      value.from?.type === 'expression',
      min,
      undefined,
      precision,
    );
    const to = _getTimeValueFromSimpleValue(
      value.to?.value,
      timeOffset,
      'to',
      value.to?.type === 'expression',
      undefined,
      max,
      precision,
    );
    return _sortTimeframeV2Values({ from, to }, min, max, precision);
  }
  return { from: undefined, to: undefined };
}

/** @internal */
function getRangePosition(newDate: TZDate, from: TZDate, to: TZDate) {
  if (from && isSameDay(newDate, from)) {
    return 'first';
  }

  if (to && isSameDay(newDate, to)) {
    return 'last';
  }

  if (
    isWithinInterval(newDate, {
      start: startOfDay(from),
      end: endOfDay(to),
    })
  ) {
    return 'inbetween';
  }
}

/** @internal */
function isRelativeDate(
  highlightType: ViewItem['highlightType'],
  selectedDate: Partial<TimeframeV2>,
): ViewItem['selectionVariant'] {
  switch (highlightType) {
    case 'first': {
      if (selectedDate.from) {
        return selectedDate.from.type === 'iso8601' ? 'absolute' : 'relative';
      }
      return undefined;
    }
    case 'last': {
      if (selectedDate.to) {
        return selectedDate.to.type === 'iso8601' ? 'absolute' : 'relative';
      }
      // if the to is selected first, an earlier date should become the from in the end, therefore
      if (!selectedDate.to && selectedDate.from) {
        return selectedDate.from.type === 'iso8601' ? 'absolute' : 'relative';
      }

      return undefined;
    }
    case 'same': {
      if (
        selectedDate.from?.type === 'expression' &&
        selectedDate.to?.type === 'expression'
      ) {
        return 'relative';
      }

      if (
        selectedDate.from?.type === 'expression' &&
        selectedDate.to?.type === 'iso8601'
      ) {
        return 'relative-absolute';
      }

      if (
        selectedDate.from?.type === 'iso8601' &&
        selectedDate.to?.type === 'expression'
      ) {
        return 'absolute-relative';
      }

      return 'absolute';
    }
    case 'inbetween':
    default: {
      return undefined;
    }
  }
}

/** @internal */
function getHighlightType(
  from: TZDate | undefined | null,
  to: TZDate | undefined | null,
  newDate: TZDate,
) {
  if (
    (to && from && isSameDay(from, newDate) && isSameDay(to, newDate)) ||
    (!to && from && isSameDay(from, newDate)) ||
    (!from && to && isSameDay(to, newDate))
  ) {
    return 'same';
  }

  if (from && to && isBefore(from, to)) {
    return getRangePosition(newDate, from, to);
  }

  if (from && to && isBefore(to, from)) {
    return getRangePosition(newDate, to, from);
  }

  return undefined;
}

/** @internal */
export function isCompleteSelection(
  type: 'single' | 'range',
  selectedDate?: Partial<TimeframeV2> | TimeframeV2 | TimeValue | null,
) {
  return isTimeframeV2(selectedDate, type)
    ? !!(selectedDate.from && selectedDate.to)
    : !!selectedDate?.value;
}

/**
 * Checks if the passed date is the selected date or part of the selected date range.
 * Depending on the selection it defines the highlightColor, the highlightType and if it is a relative timeframe.
 * @internal
 */
export const getHighlightingProps = (
  date: TZDate,
  state: Pick<CalendarState, 'hoveredDate' | 'selectedDate' | 'type'>,
): Pick<
  ViewItem,
  | 'isSelected'
  | 'highlightColor'
  | 'highlightType'
  | 'selectionVariant'
  | 'isIncompleteSelection'
> => {
  const { selectedDate, hoveredDate, type } = state;
  if (!selectedDate) {
    return {};
  }

  const configuredTimezone = getTimezone();

  if (isTimeframeV2(selectedDate, type)) {
    const { from: selectedFrom, to: selectedTo } = selectedDate;
    if (!selectedFrom && !selectedTo) {
      return {};
    }
    const from = selectedFrom?.absoluteDate
      ? new TZDateMini(selectedFrom.absoluteDate, configuredTimezone)
      : null;
    const to = selectedTo?.absoluteDate
      ? new TZDateMini(selectedTo.absoluteDate, configuredTimezone)
      : null;

    const isSelected = !!(
      (from && isSameDay(date, from)) ||
      (to && isSameDay(date, to))
    );
    const safeFromDate = from ?? hoveredDate;
    const safeToDate = to ?? hoveredDate;

    const highlightType = getHighlightType(safeFromDate, safeToDate, date);
    const isEntireRangeSelected =
      !!selectedFrom && !!selectedTo && !!highlightType;

    const highlightColor =
      isSelected || isEntireRangeSelected ? 'primary' : 'neutral';

    const isRelative = isSelected
      ? isRelativeDate(highlightType, selectedDate)
      : undefined;

    return {
      highlightType,
      highlightColor,
      isSelected,
      isIncompleteSelection: !isEntireRangeSelected,
      selectionVariant: isRelative,
    };
  }

  const isSelected = isSameDay(
    date,
    new TZDateMini(selectedDate.absoluteDate, configuredTimezone),
  );

  if (isSelected) {
    return {
      isSelected: true,
      highlightType: 'same',
      highlightColor: 'primary',
      isIncompleteSelection: false,
      selectionVariant:
        selectedDate.type === 'expression' ? 'relative' : 'absolute',
    };
  }

  return {
    isSelected: false,
  };
};

/**
 * Returns the number of days from the start of the week until the current date.
 * @internal
 */
export function getWeekStartOrEnd(
  indicator: 'startOfWeek' | 'endOfWeek',
  activeDate: TZDate,
  weekStartsOn: FirstDayOfWeek,
) {
  const start =
    indicator === 'startOfWeek'
      ? startOfWeek(activeDate, { weekStartsOn })
      : endOfWeek(activeDate, { weekStartsOn });

  return (
    (intervalToDuration({
      start,
      end: activeDate,
    }).days ?? 0) * -1
  );
}

/**
 * Returns the next position depending on the current.
 * @internal
 */
export function getPosition(
  position?: CalendarState['position'],
  value?: TimeValue | Partial<TimeframeV2> | TimeframeV2 | null,
) {
  // single selection
  if (!position) {
    return undefined;
  }

  // If there is no position it always is a range selection
  if (
    isCompleteSelection('range', value) ||
    value === null ||
    (!(value as Partial<TimeframeV2>).from?.value &&
      !(value as Partial<TimeframeV2>).to?.value)
  ) {
    return 'from';
  }

  return position === 'from' ? 'to' : 'from';
}

/**
 * Either assigns and sanitizes the passed timeValue to the Timeframe or returns the TimeValue in case of single selection.
 * @internal
 */
export function getDateFromSelection(
  oldSelection: TimeValue | Partial<TimeframeV2> | null,
  type: CalendarType,
  timeValue?: TimeValue,
  position?: CalendarState['position'],
  min?: string,
  max?: string,
  precision?: 'minutes' | 'seconds' | 'milliseconds',
) {
  if (isTimeframeV2(oldSelection, type)) {
    const tmp = oldSelection ? { ...oldSelection } : {};
    tmp[position ?? 'from'] = timeValue;
    // Clears the to date in case a new selection starts.
    if (position === 'from') {
      tmp['to'] = undefined;
    }

    return _sortTimeframeV2Values(tmp, min, max, precision);
  }

  return timeValue ?? null;
}

/** @internal */
export function getTimeOffsetFromInitialValue<Type extends CalendarType>(
  value?: CalendarStrictValueType<Type>,
  type: CalendarType = 'single',
  min: TimeValue | null = null,
  max: TimeValue | null = null,
  precision: CalendarProps<Type>['precision'] = 'milliseconds',
): StringTimeOffset {
  if (isTimeframeV2(value, type)) {
    if (!value) {
      return { from: 'startOfDay', to: 'endOfDay' };
    }

    let from = value['from']?.absoluteDate;
    let to = value['to']?.absoluteDate;
    const configuredTimezone = getTimezone();

    const fromIsAtStartOfDay =
      from &&
      from ===
        startOfDay(new TZDateMini(from, configuredTimezone)).toISOString();
    const fromIsMin =
      min &&
      removePrecision(from, precision) ===
        removePrecision(min?.absoluteDate, precision);

    // we need to check if the absoluteDate is set and would match the start of day in the configured timezone (which could be on a different date than the absoluteDate) or if a relative expressions is used
    if (
      !from ||
      fromIsAtStartOfDay ||
      value['from']?.type === 'expression' ||
      fromIsMin
    ) {
      from = 'startOfDay';
    }

    const toIsAtEndOfDay =
      to &&
      to === endOfDay(new TZDateMini(to, configuredTimezone)).toISOString();
    const toIsMax =
      max &&
      removePrecision(to, precision) ===
        removePrecision(max?.absoluteDate, precision);

    // we need to check if the absoluteDate is set and would match the end of day in the configured timezone (which could be on a different date than the absoluteDate) or if a relative expressions is used
    if (!to || toIsAtEndOfDay || value.to.type === 'expression' || toIsMax) {
      to = 'endOfDay';
    }

    return {
      from,
      to,
    };
  }

  if (typeof value === 'string') {
    return isExpressionString(value) ? 'startOfDay' : value;
  }

  return 'startOfDay';
}
